import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getApiErrorMessage } from "../../../services/apiError";
import {
  XIcon,
  OfficeBuildingIcon,
  MailIcon,
  PhoneIcon,
  LocationMarkerIcon,
  PhotographIcon,
  HashtagIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";

const CreateResortModal = ({ isOpen, onClose, onResortCreated }) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [formData, setFormData] = useState({
    resort_name: "",
    resort_code: "",
    resort_description: "",
    resort_phone: "",
    resort_email: "",
    resort_address_one: "",
    resort_address_two: "",
    resort_logo: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "resort_code"
        ? value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)
        : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/resort`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Property created successfully");
        // Reset form
        setFormData({
          resort_name: "",
          resort_code: "",
          resort_description: "",
          resort_phone: "",
          resort_email: "",
          resort_address_one: "",
          resort_address_two: "",
          resort_logo: "",
        });
        onClose();
        try {
          onResortCreated?.();
        } catch (err) {
          console.error("onResortCreated callback error:", err);
        }
      } else {
        // The API returns actionable field-level validation messages in
        // `data`; use those before falling back to a generic notification.
        const payload =
          typeof response.json === "function"
            ? await response.json().catch(() => null)
            : null;
        const message = getApiErrorMessage(payload, "Failed to create Property");

        toast.error(message);
        console.error("Failed to create Resort", {
          status: response.status,
          payload,
        });
      }
    } catch (error) {
      toast.error("Error occurred while creating Property");
      console.error("Error occurred while creating Resort:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform transition-all">
            <div className="modal-container">
              {/* Header */}
              <div className="modal-header">
                <div className="flex items-center space-x-3">
                  <div className="modal-icon">
                    <OfficeBuildingIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="modal-title">Create New Property</h3>
                    <p className="modal-subtitle">Add a new property to your portfolio</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="modal-close-btn"
                  type="button"
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {/* Grid Layout for Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Property Name */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_name" className="form-label">
                        <OfficeBuildingIcon className="form-label-icon" />
                        Property Name *
                      </label>
                      <input
                        type="text"
                        id="resort_name"
                        name="resort_name"
                        value={formData.resort_name}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., Sunset Resort & Spa"
                        required
                      />
                    </div>

                    {/* Property Code */}
                    <div className="form-group">
                      <label htmlFor="resort_code" className="form-label">
                        <HashtagIcon className="form-label-icon" />
                        Property Code *
                      </label>
                      <input
                        type="text"
                        id="resort_code"
                        name="resort_code"
                        value={formData.resort_code}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., BONSTINO or 100001"
                        pattern="[A-Z0-9]{2,12}"
                        maxLength={12}
                        autoCapitalize="characters"
                        spellCheck="false"
                        required
                      />
                      <p className="form-helper-text">
                        Use 2-12 uppercase letters and numbers only.
                      </p>
                    </div>

                    {/* Phone */}
                    <div className="form-group">
                      <label htmlFor="resort_phone" className="form-label">
                        <PhoneIcon className="form-label-icon" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="resort_phone"
                        name="resort_phone"
                        value={formData.resort_phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., +1 234 567 8900"
                        required
                      />
                    </div>

                    {/* Email */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_email" className="form-label">
                        <MailIcon className="form-label-icon" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="resort_email"
                        name="resort_email"
                        value={formData.resort_email}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="e.g., contact@property.com"
                        required
                      />
                    </div>

                    {/* Description */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_description" className="form-label">
                        <DocumentTextIcon className="form-label-icon" />
                        Property Description *
                      </label>
                      <textarea
                        id="resort_description"
                        name="resort_description"
                        value={formData.resort_description}
                        onChange={handleChange}
                        className="form-textarea"
                        placeholder="Describe your property..."
                        rows="4"
                        required
                      />
                    </div>

                    {/* Address Line 1 */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_address_one" className="form-label">
                        <LocationMarkerIcon className="form-label-icon" />
                        Address Line 1 *
                      </label>
                      <input
                        type="text"
                        id="resort_address_one"
                        name="resort_address_one"
                        value={formData.resort_address_one}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Street address, P.O. box"
                        required
                      />
                    </div>

                    {/* Address Line 2 */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_address_two" className="form-label">
                        <LocationMarkerIcon className="form-label-icon" />
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        id="resort_address_two"
                        name="resort_address_two"
                        value={formData.resort_address_two}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Apartment, suite, unit, building, floor, etc."
                      />
                    </div>

                    {/* Logo URL */}
                    <div className="form-group md:col-span-2">
                      <label htmlFor="resort_logo" className="form-label">
                        <PhotographIcon className="form-label-icon" />
                        Logo URL
                      </label>
                      <input
                        type="url"
                        id="resort_logo"
                        name="resort_logo"
                        value={formData.resort_logo}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="https://example.com/logo.png"
                      />
                      <p className="form-helper-text">
                        Provide a direct URL to your property logo image
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </>
                    ) : (
                      'Create Property'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style jsx>{`
        .modal-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
          max-height: 60vh;
          overflow-y: auto;
        }

        .modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb {
          background: #8b5cf6;
          border-radius: 4px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover {
          background: #7c3aed;
        }

        .form-group {
          margin-bottom: 0;
        }

        .form-label {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-label-icon {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
          color: #8b5cf6;
        }

        .form-input,
        .form-textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-input:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #8b5cf6;
          background: white;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .form-helper-text {
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: #6b7280;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
        }

        .btn-secondary {
          padding: 0.75rem 1.5rem;
          background: white;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }

        .btn-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-primary {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          border: none;
          border-radius: 0.75rem;
          font-weight: 600;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #7c3aed, #4f46e5);
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(139, 92, 246, 0.4);
        }

        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
};

export default CreateResortModal;
